import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import Tag from '@/components/base/Tag';
import ReadingProgress from './components/ReadingProgress';
import ArticleAside from './components/ArticleAside';
import {
  formatDate,
  getArticleBySlug,
  getCategory,
  getRelatedArticles,
} from '@/utils/content';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);
  const category = article ? getCategory(article.category) : undefined;

  useSeo({
    title: article ? `${article.title} | 美日吃瓜` : '档案未找到 | 美日吃瓜',
    description: article
      ? `${article.excerpt.slice(0, 110)}　美日吃瓜「${category?.name ?? ''}」栏目深度档案，附来源说明与时间线梳理。`
      : '该档案不存在或已被调整，请返回首页浏览其他内容。',
    keywords: article
      ? `美日吃瓜,${article.tags.join(',')},${category?.name ?? ''}`
      : '美日吃瓜',
    path: `/article/${slug}`,
    type: 'article',
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              datePublished: article.date,
              dateModified: article.date,
              inLanguage: 'zh-CN',
              articleSection: category?.name,
              keywords: article.tags.join(','),
              author: { '@type': 'Person', name: article.author },
              publisher: { '@type': 'Organization', name: '美日吃瓜' },
              image: article.cover,
              mainEntityOfPage: `/article/${slug}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category?.name ?? '栏目',
                  item: `/category/${article.category}`,
                },
                { '@type': 'ListItem', position: 3, name: article.title, item: `/article/${slug}` },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
          <i className="ri-file-warning-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          这份档案不存在
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          链接可能已经失效，或者稿件正在重新核对。回到首页可以看到最新更新的档案。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  return (
    <>
      <ReadingProgress />
      <Breadcrumbs
        items={[
          { label: category?.name ?? '栏目', to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article>
        <header className="border-b border-background-300/70 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
            <div className="max-w-3xl">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-100 px-3 py-1 font-label text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={category?.icon ?? 'ri-article-line'} />
                </span>
                {category?.name ?? '档案'}
              </Link>

              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>

              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {article.excerpt}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-user-3-line" />
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  {formatDate(article.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  约 {article.readTime} 分钟
                </span>
                <span className="flex items-center gap-1.5 text-accent-700">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-fire-line" />
                  </span>
                  热度 {article.heat.toLocaleString('zh-CN')}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Tag key={tag} to={`/category/${article.category}`}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <figure className="mb-8">
                <div className="h-56 w-full overflow-hidden rounded-lg border border-background-300/70 md:h-80">
                  <img
                    src={article.cover}
                    alt={article.coverAlt}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-2.5 font-label text-xs text-foreground-500">
                  配图：{article.coverAlt}（示意图，非事件现场照片）
                </figcaption>
              </figure>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-accent-900">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className="ri-information-line" />
                  </span>
                  本文依据监管公告、法庭文书与可公开检索的记录整理，涉及尚未证实的部分均已明确标注。若当事方提出有效异议，我们会在文末更新说明。
                </p>
              </div>

              <div className="mt-8 space-y-9">
                {article.sections.map((section, index) => (
                  <section key={section.id} id={section.id} className="scroll-mt-32">
                    <h2 className="flex items-start gap-3 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                      <span className="mt-0.5 font-label text-sm text-primary-600">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      {section.heading}
                    </h2>
                    <div className="mt-3 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph.slice(0, 18)}
                          className="text-sm leading-loose text-foreground-700 md:text-base"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-col gap-4 border-t border-background-300/70 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm leading-relaxed text-foreground-600">
                  掌握这条线索的更多材料？欢迎投递给编辑部，我们会做独立核对。
                </p>
                <Link
                  to="/contact"
                  className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-send-plane-line" />
                  </span>
                  投递线索
                </Link>
              </div>
            </div>

            <ArticleAside sections={article.sections} category={category} related={related} />
          </div>
        </div>
      </article>

      <section className="border-t border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="mb-2 font-label text-xs uppercase tracking-[0.28em] text-primary-600">
                Keep Reading
              </p>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                继续读相关档案
              </h2>
            </div>
            <Link
              to="/"
              className="hidden whitespace-nowrap font-label text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex sm:items-center sm:gap-1"
            >
              回到首页
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.slug} article={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}